import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STORAGE_KEY = "lumioo_active_video_job";
const POLL_MS = 3500;

const VideoJobContext = createContext(null);

export function VideoJobProvider({ children }) {
  const [activeJob, setActiveJob] = useState(null); // { job_id, topic, status, stage, started_at }
  const [result, setResult] = useState(null);
  const pollRef = useRef(null);
  const finishHandlersRef = useRef([]);

  // Restore from localStorage on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved?.job_id) {
        setActiveJob(saved);
      }
    } catch { /* ignore */ }
  }, []);

  const clearActive = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setActiveJob(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const onDone = useCallback((handler) => {
    finishHandlersRef.current.push(handler);
    return () => {
      finishHandlersRef.current = finishHandlersRef.current.filter((h) => h !== handler);
    };
  }, []);

  // Background poll lifecycle
  useEffect(() => {
    if (!activeJob?.job_id) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return; // already polling

    const tick = async () => {
      try {
        const { data } = await api.get(`/videos/jobs/${activeJob.job_id}`);
        setActiveJob((cur) => {
          if (!cur || cur.job_id !== activeJob.job_id) return cur;
          const next = { ...cur, status: data.status, stage: data.stage };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
          return next;
        });
        if (data.status === "done" && data.result) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setResult({ job_id: activeJob.job_id, ...data.result });
          localStorage.removeItem(STORAGE_KEY);
          setActiveJob(null);
          toast.success("Video ready 🎬");
          finishHandlersRef.current.forEach((h) => { try { h({ ok: true, result: data.result, job_id: activeJob.job_id }); } catch {} });
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          localStorage.removeItem(STORAGE_KEY);
          setActiveJob(null);
          toast.error(data.error || "Video generation failed");
          finishHandlersRef.current.forEach((h) => { try { h({ ok: false, error: data.error }); } catch {} });
        }
      } catch {
        // network blip — keep retrying
      }
    };
    // immediate first tick, then interval
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [activeJob?.job_id]);

  const startJob = useCallback(async ({ topic, tone, length, voice, language }) => {
    if (activeJob) {
      toast.error("A video is already being generated — wait or cancel.");
      throw new Error("job-already-active");
    }
    const { data } = await api.post("/videos/generate", { topic, tone, length, voice, language });
    const job = {
      job_id: data.job_id,
      topic,
      tone, length, voice, language,
      status: data.status || "queued",
      stage: "queued",
      started_at: new Date().toISOString(),
    };
    setActiveJob(job);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(job));
    setResult(null);
    return job;
  }, [activeJob]);

  const clearResult = useCallback(() => setResult(null), []);

  return (
    <VideoJobContext.Provider value={{ activeJob, result, startJob, clearActive, clearResult, onDone }}>
      {children}
    </VideoJobContext.Provider>
  );
}

export const useVideoJob = () => {
  const ctx = useContext(VideoJobContext);
  if (!ctx) throw new Error("useVideoJob must be used within VideoJobProvider");
  return ctx;
};
